type Labels = {
  preview_caption: string
  preview_owned_by: string
  preview_brewery_placeholder: string
  preview_no_contact: string
  preview_email_label: string
  preview_phone_label: string
}

type Props = {
  name: string
  contactEmail: string
  contactPhone: string
  labels: Labels
}

// A still of /f/:token, rendered live while the brewery is being created.
//
// Deliberately NOT extracted from pages/public_kegs/anonymous.tsx: that section is interactive
// (a "reveal" button that POSTs a KegContactReveal, mailto:/tel: links), this one is a static
// thumbnail always shown in the revealed state. Sharing them would mean a component steered by a
// mode boolean — worse than these fifteen lines of JSX. The trade-off: restyling the public page
// does not reach this preview.
export function PublicKegPreview({ name, contactEmail, contactPhone, labels }: Props) {
  const hasContact = Boolean(contactEmail.trim() || contactPhone.trim())

  return (
    <figure className="m-0">
      <figcaption className="text-xs text-ink-faint">{labels.preview_caption}</figcaption>

      <div className="mt-2 rounded-xl border-[1.5px] border-dashed border-line bg-paper px-4 py-4">
        <p className="text-xs text-ink-faint">{labels.preview_owned_by}</p>
        <p
          className={`font-display text-xl leading-tight ${name.trim() ? 'text-ink' : 'text-ink-faint'}`}
        >
          {name.trim() || labels.preview_brewery_placeholder}
        </p>

        {hasContact ? (
          <div className="mt-3 space-y-1.5 border-t border-line pt-3">
            {contactEmail.trim() && (
              <ContactLine label={labels.preview_email_label} value={contactEmail} />
            )}
            {contactPhone.trim() && (
              <ContactLine label={labels.preview_phone_label} value={contactPhone} />
            )}
          </div>
        ) : (
          // The failure state, on purpose: this is exactly what a finder reads today when a
          // brewery has no contact details. Seeing it is what makes the fields worth filling.
          //
          // Not in flame-deep though — that colour means "you did something wrong" everywhere else
          // (see Field). Nothing is wrong yet; this is a statement about the public page, and the
          // sentence carries the tension on its own. The real red arrives on submit.
          <p className="mt-3 border-t border-line pt-3 text-sm font-semibold text-ink-soft">
            {labels.preview_no_contact}
          </p>
        )}
      </div>
    </figure>
  )
}

function ContactLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-3 text-sm">
      <span className="text-ink-soft">{label}</span>
      <span className="truncate font-semibold text-leaf-deep">{value}</span>
    </div>
  )
}
